import type { CSSProperties } from 'react'
import { cx } from '@/lib/cx'
import styles from './VoiceRipple.module.css'

/** Props for {@link VoiceRipple}. */
export interface VoiceRippleProps {
  /** Number of rings. Default `4`. */
  rings?: number
  /** Seconds one ring takes to travel from the centre to the edge. Default `6`. */
  period?: number
  /**
   * Breathe (emit rings). When `false` the rings rest as a calm, evenly spaced set, which is
   * the reduced-motion and offscreen state.
   */
  playing?: boolean
  /** `prism` (default): iridescent bands. `slate`: a fine graphite hairline. */
  tone?: 'prism' | 'slate'
  /** Show the soft prism glow in the centre. Default `true`. */
  glow?: boolean
  className?: string
}

/**
 * Small, fixed speed offsets per ring. The rings drift out of phase, so the pattern never
 * repeats exactly: it reads as breathing, not as a loop.
 */
const DRIFT = [0, 0.083, -0.051, 0.121, -0.094, 0.037, 0.066]

/**
 * The Vokarta "voice ripple": concentric rings breathing out from a source, like sound.
 * Pure CSS (transform + opacity, compositor only). It fills its box, so size it with the
 * `className`. Decorative: always `aria-hidden`.
 *
 * @example <VoiceRipple className={styles.ripple} playing={inView && !reducedMotion} />
 */
export function VoiceRipple({
  rings = 4,
  period = 6,
  playing = true,
  tone = 'prism',
  glow = true,
  className,
}: VoiceRippleProps) {
  return (
    <div
      className={cx(styles.root, styles[tone], playing && styles.playing, className)}
      aria-hidden="true"
    >
      {glow ? <span className={styles.glow} /> : null}
      {Array.from({ length: rings }, (_, index) => {
        const duration = period * (1 + DRIFT[index % DRIFT.length])
        const style = {
          '--ring-index': index,
          '--ring-rest': (index + 1) / rings,
          '--ring-duration': `${duration.toFixed(3)}s`,
          '--ring-delay': `${(-(duration * index) / rings).toFixed(3)}s`,
        } as CSSProperties
        return <span key={index} className={styles.ring} style={style} />
      })}
    </div>
  )
}
